// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}`;
  }
  // A server render has no window to read an origin from. Reaching here used to throw, which
  // took down whatever data load was in progress; the configured root URL answers the same
  // question, and an empty string at least fails as a bad request rather than a crash.
  const rootURL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  return rootURL ? rootURL.replace(/\/$/, '') : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.text().then(text => {
        let data = {};
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseErr) {
            data = { error: text };
          }
        }
        let e = new Error(data.message || data.error || res.statusText || 'Request failed');
        // Attach the full response data and status to the error
        e.status = res.status;
        e.statusText = res.statusText;
        if (typeof data === 'object' && data !== null) {
          e = Object.assign(e, data);
        }

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// ================ Messaging API ================ //

// Get all conversations for a user
//
// See `server/api/messaging.js` to see what data should be sent.
export const getConversations = userId => {
  const url = `/api/messaging/conversations?userId=${userId}`;
  return request(url, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

// Get messages for a specific conversation
//
// See `server/api/messaging.js` to see what data should be sent.
export const getMessages = (conversationId, limit = 50, skip = 0) => {
  const url = `/api/messaging/messages?conversationId=${conversationId}&limit=${limit}&skip=${skip}`;
  return request(url, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

// Send a message
//
// See `server/api/messaging.js` to see what data should be sent in the body.
export const sendMessage = body => {
  return request('/api/messaging/send', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Mark messages as read
//
// See `server/api/messaging.js` to see what data should be sent in the body.
export const markMessagesAsRead = body => {
  return request('/api/messaging/mark-read', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// ================ Slots API ================ //

export const createSlot = body => {
  return request('/api/slots/create', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

 export const createNonRecurringSlotsBatch = body => {
   return request('/api/slots/create-batch', {
     method: methods.POST,
     headers: { 'Content-Type': 'application/json' },
     body: JSON.stringify(body),
   });
 };

export const createRecurringSlots = body => {
  return request('/api/slots/create-recurring', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const getSlots = listingId => {
  const url = `/api/slots/list?listingId=${listingId}`;
  return request(url, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const updateSlot = body => {
  return request('/api/slots/update', {
    method: methods.PUT,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const updateSlotsByVariant = body => {
  return request('/api/slots/update-by-variant', {
    method: methods.PUT,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// The order a provider dragged this listing's slots into, saved for the availability
// editor alone. Everything else still reads slots in date order.
export const reorderSlots = body => {
  return request('/api/slots/reorder', {
    method: methods.PUT,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const updateListingTimezone = body => {
  return request('/api/slots/update-timezone', {
    method: methods.PUT,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// ================ Booking API ================ //

// Create a booking with atomic seat reservation
// This should be called BEFORE initiating Sharetribe payment
export const createBooking = body => {
  return request('/api/bookings/create', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Confirm a booking (called after provider accepts)
export const confirmBooking = body => {
  return request('/api/bookings/confirm', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// Cancel a booking and release seats
export const cancelBooking = body => {
  return request('/api/bookings/cancel', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

/* Write 'refunded' onto our copy of a booking. It moves no money — Sharetribe's cancel
   and decline transitions carry the payment reversal — and it releases no seats. See
   markBookingRefundedLogic on the server for why that matters. */
export const markBookingRefunded = body => {
  return request('/api/bookings/refund', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const deleteSlot = slotId => {
  const url = `/api/slots/${slotId}`;
  return request(url, {
    method: methods.DELETE,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const deleteSlotsByVariant = body => {
  return request('/api/slots/delete-by-variant', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

// ================ Commission administration ================ //
//
// Every one of these is gated server-side on the administrator list; see
// server/api-util/commissionAdmin.js. Hiding the screen is not the permission.

export const getCommissionAccess = () =>
  request('/api/commission/access', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });

export const getCommissionProviders = (page = 1, perPage = 10, search = '') => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  return request(`/api/commission/providers?page=${page}&perPage=${perPage}${searchParam}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getProviderCommission = userId =>
  request(`/api/commission/provider/${userId}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });

export const setProviderCommission = (userId, body) =>
  request(`/api/commission/provider/${userId}`, {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const getCommissionListings = (page = 1, perPage = 10, search = '') => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  return request(`/api/commission/listings?page=${page}&perPage=${perPage}${searchParam}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getListingCommission = listingId =>
  request(`/api/commission/listing/${listingId}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });

export const setListingCommission = (listingId, body) =>
  request(`/api/commission/listing/${listingId}`, {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const removeListingCommission = listingId =>
  request(`/api/commission/listing/${listingId}`, {
    method: methods.DELETE,
    headers: { 'Content-Type': 'application/json' },
  });

export const removeProviderCommission = userId =>
  request(`/api/commission/provider/${userId}`, {
    method: methods.DELETE,
    headers: { 'Content-Type': 'application/json' },
  });

export const getItineraryEvents = (userId, start, end) => {
  let url = `/api/itinerary?userId=${userId}`;
  if (start && end) {
    url += `&start=${start.toISOString()}&end=${end.toISOString()}`;
  }
  return request(url, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getProviderItineraryEvents = (userId, start, end) => {
  let url = `/api/itinerary/provider?userId=${userId}`;
  if (start && end) {
    url += `&start=${start.toISOString()}&end=${end.toISOString()}`;
  }
  return request(url, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

// Everything the provider dashboard shows for one provider: their sessions, who is
// booked, who is waiting on an answer, how many people are watching, and the payout
// history behind the chart.
//
// See `server/api/providerDashboard.js` for the shape of the response.
export const getProviderDashboard = userId => {
  const url = `/api/provider/dashboard?userId=${userId}`;
  return request(url, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const addItineraryEvent = body => {
  return request('/api/itinerary/add', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const deleteItineraryEvent = eventId => {
  const url = `/api/itinerary/${eventId}`;
  return request(url, {
    method: methods.DELETE,
    headers: { 'Content-Type': 'application/json' },
  });
};

// A booking the provider took themselves, at the gate or on another site. Who they are
// comes from the request's own cookie on the server, never from here; see
// server/api/offlineBookings.js.
export const createOfflineBooking = body =>
  request('/api/bookings/offline', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const deleteOfflineBooking = bookingId =>
  request(`/api/bookings/offline/${bookingId}`, {
    method: methods.DELETE,
    headers: { 'Content-Type': 'application/json' },
  });